import { useMemo, type FC } from 'react';
import { useTheme } from 'styled-components';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import { Box } from 'components/Box';
import {
  AnnualAmountsChart,
  AnnualAmountsTable,
  CalculationSummary,
} from 'components/Calculator/components';
import { getFormattedAmount } from 'helpers/getFormattedAmount';
import { useContentfulLocale } from 'hooks/useContentfulLocale';
import { getTotalDividendReturn } from 'helpers/dividendCalculator/getTotalDividendReturn';
import { getMonthlyDividendIncomeReinvested } from 'helpers/dividendCalculator/getMonthlyDividendIncomeReinvested';
import { getAverageMonthlyIncome } from 'helpers/dividendCalculator/getAverageMonthlyIncome';
import { FlexContainer } from 'components/FlexContainer';
import { getDividendReturnsBreakdown } from './getDividendReturnsBreakdown';

import type { DividendCalculatorFormState } from '../types';

interface DividendCalculatorResultsProps {
  state: DividendCalculatorFormState;
}

export const DividendCalculatorResults: FC<DividendCalculatorResultsProps> = ({ state }) => {
  const {
    translations: { currencySymbol },
  } = useContentfulLocale();

  const { breakpoints } = useTheme();

  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const sharePrice = Number(state.sharePrice) || 0;
  const numberOfShares = Number(state.numberOfShares) || 0;
  const holdingPeriod = Number(state.holdingPeriod) || 0;
  const annualDividendYield = Number(state.annualDividendYield) || 0;
  const annualContribution = Number(state.annualContribution) || 0;
  const expectedAnnualStockAppreciation = Number(state.expectedAnnualStockAppreciation) || 0;
  const expectedDividendGrowthRate = Number(state.expectedDividendGrowthRate) || 0;

  const dividendReturnsBreakdown = getDividendReturnsBreakdown({
    sharePrice,
    numberOfShares,
    holdingPeriod,
    annualDividendYield,
    annualContribution,
    dividendReinvestmentPlan: state.dividendReinvestmentPlan,
    expectedAnnualStockAppreciation,
    expectedDividendGrowthRate,
  });
  const dividendReturn = getTotalDividendReturn({
    sharePrice,
    numberOfShares,
    holdingPeriod,
    annualDividendYield,
    annualContribution,
    dividendReinvestmentPlan: state.dividendReinvestmentPlan,
    expectedAnnualStockAppreciation,
    expectedDividendGrowthRate,
  });

  const formattedEstimatedDividendReturn = getFormattedAmount(dividendReturn, 2, currencySymbol);

  const monthlyDividendIncomes = useMemo(
    () =>
      state.dividendReinvestmentPlan === 'yes'
        ? getMonthlyDividendIncomeReinvested(
            sharePrice,
            numberOfShares,
            holdingPeriod,
            annualDividendYield,
            annualContribution,
            expectedAnnualStockAppreciation,
            expectedDividendGrowthRate
          )
        : getAverageMonthlyIncome(
            sharePrice,
            numberOfShares,
            holdingPeriod,
            annualDividendYield,
            annualContribution,
            expectedAnnualStockAppreciation,
            expectedDividendGrowthRate
          ),
    [
      sharePrice,
      numberOfShares,
      holdingPeriod,
      annualDividendYield,
      annualContribution,
      expectedAnnualStockAppreciation,
      expectedDividendGrowthRate,
      state.dividendReinvestmentPlan,
    ]
  );

  const title =
    state.dividendReinvestmentPlan === 'yes'
      ? 'Dividend income reinvested'
      : 'Average monthly income';

  return (
    <div>
      <CalculationSummary
        title="Estimated dividend return"
        headerAmount={formattedEstimatedDividendReturn}
        summary={dividendReturnsBreakdown}
      />
      <Box padding={5}>
        <FlexContainer justifyContent="center">
          <Box maxWidth={isMobile ? '90%' : '100%'}>
            <AnnualAmountsChart
              balances={monthlyDividendIncomes}
              title={title}
              columnLabel="Avg. monthly income"
            />
          </Box>
        </FlexContainer>
        <AnnualAmountsTable balances={monthlyDividendIncomes} />
      </Box>
    </div>
  );
};
